import { useOktaAuth } from '@okta/okta-react';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { rem, Tabs, TextAlign } from '@triumph/design-system';
import { Tag } from 'primereact/tag';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { useLoadingProvider } from '@/components/providers/LoadingContextProvider';
import { DeepLinkBanner } from '@/components/shared/components/DeepLinkBanner';
import {
    getTabBadgeStyle,
    TAB_BADGE_PARTIAL_STYLE,
} from '@/components/shared/constants/conditionsBadge';
import {
    getApplicantDocuments,
    getApplicantDueDiligence,
    getApplicantReasonCodes,
    getApplicantTags,
} from '@/components/shared/api/endpoints/applicant';
import { useRefreshStatusLoadPayMutation } from '@/components/shared/api/mutations/applicant';
import {
    useApplicantActivityQuery,
    useApplicantRelatedAccountsQuery,
} from '@/components/shared/api/queries/applicant';
import { useConditionsQuery } from '@/components/shared/api/queries/conditions';
import { FOCUS_STALE_TIME_MS } from '@/components/shared/api/queries/staleTime';
import { Config } from '@/components/shared/utils/config';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';

import './Applicant.print.css';
import {
    ApplicantTab,
    Container,
    ContentWrapper,
    TabBadgeSpinner,
    TabPanel,
} from './Applicant.StyledComponents';
import {
    ApplicantActivityResponse,
    ApplicantTabId,
    CONDITIONS_TAB_TEST_ID,
    DecisionDetailStatuses,
} from './Applicant.types';
import { getExternalActivityCount } from './Applicant.utils';
import { ApplicantActivity } from './components/ApplicantActivity';
import { ApplicantConditions } from './components/ApplicantConditions';
import {
    sortByFixedRank,
    sortDeletedConditions,
} from './components/ApplicantConditions/ApplicantConditions.utils';
import { ApplicantDocument } from './components/ApplicantDocument';
import { ApplicantHeader } from './components/ApplicantHeader';
import { ApplicantRelatedAccounts } from './components/ApplicantRelatedAccounts';
import { RELATED_ACCOUNTS_PARTIAL_COUNT_TITLE } from './components/ApplicantRelatedAccounts/constants';
import { ApplicantStatus } from './components/ApplicantStatus';
import { ApplicationTab, ApplicationTabLabel } from './components/ApplicationTab';
import { ApplicantTags } from './components/ApplicantTags';
import { DueDiligence } from './components/DueDiligence';
import { OutOfSyncBanner } from './components/OutOfSyncBanner';
import { PrintAggregate } from './components/PrintAggregate';

export const Applicant = () => {
    const printSectionRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const { applicant, applicantId, applicantLoadPayStatus } = useApplicantContext();
    const { isLoading: isApplicantLoading } = useLoadingProvider();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTabId = searchParams.get('tab');
    const isPageReady = Boolean(applicant) && !isApplicantLoading;
    const conditionIdParam = searchParams.get('conditionId');
    const noteIdParam = searchParams.get('noteId');
    const nmiIdParam = searchParams.get('nmiId');
    const focusConditionId = isPageReady ? conditionIdParam : null;
    const focusActivityNoteId =
        isPageReady && activeTabId === ApplicantTabId.Activity ? noteIdParam : null;
    const focusConditionNoteId =
        isPageReady && activeTabId === ApplicantTabId.Conditions ? noteIdParam : null;
    const focusActivityNmiId =
        isPageReady && activeTabId === ApplicantTabId.Activity ? nmiIdParam : null;

    // Tracks the currently-selected tab so ApplicantActivity can tell when it's
    // been (re-)activated — the design-system Tabs keeps every tab's content
    // mounted and only toggles CSS display, so mount alone can't signal this.
    const [selectedTabId, setSelectedTabId] = useState<string | undefined>(
        activeTabId || undefined,
    );

    // The design-system Tabs is uncontrolled, so external navigations (e.g. a
    // document's condition link) only take effect by remounting it. Bump this
    // key only when a *new* tab param arrives — never when params are cleared on
    // a manual tab click, which would otherwise reset to the default tab.
    const [tabsRemountKey, setTabsRemountKey] = useState(0);
    const lastForcedTab = useRef<string | null>(activeTabId);
    useEffect(() => {
        if (!activeTabId) {
            lastForcedTab.current = null;
            return;
        }
        if (activeTabId !== lastForcedTab.current) {
            lastForcedTab.current = activeTabId;
            setTabsRemountKey((k) => k + 1);
            // The remounted Tabs opens this tab without calling onTabSelected, so a lazy tab would never activate.
            setSelectedTabId(activeTabId);
        }
    }, [activeTabId]);

    const {
        data: conditionsData,
        isLoading: isConditionsLoading,
        isLoadingError: isConditionsLoadingError,
        refetch: refetchConditions,
    } = useConditionsQuery({
        applicantId: applicantId ?? '',
        enabled: Boolean(applicantId),
    });
    const openConditions = useMemo(
        () => sortByFixedRank((conditionsData ?? []).filter((c) => !c.resolved && !c.isDeleted)),
        [conditionsData],
    );
    const closedConditions = useMemo(
        () =>
            sortByFixedRank((conditionsData ?? []).filter((c) => c.resolved && !c.isDeleted)),
        [conditionsData],
    );
    const deletedConditions = useMemo(
        () => sortDeletedConditions((conditionsData ?? []).filter((c) => c.isDeleted)),
        [conditionsData],
    );
    const openConditionsCount = openConditions.length;
    const isConditionsCountKnown = !isConditionsLoading && !isConditionsLoadingError;

    const [hasLoadPayRefreshed, setHasLoadPayRefreshed] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const isPrintingRef = useRef(false);
    const handlePrintClickRef = useRef<() => void>(() => {});
    const [showOutOfSyncBanner, setShowOutOfSyncBanner] = useState(false);
    // Suppress the banner for 10s after any manual refresh to absorb in-flight refetches
    const suppressBannerUntil = useRef<number>(0);
    const previousActivitySnapshot = useRef<ApplicantActivityResponse | null>(null);
    // A return refetches every stale on-screen query, so the activity it brings back is already on screen.
    const absorbFocusRefetch = useRef(false);

    const { mutateAsync: refreshStatusLoadPay, isPending: isRefreshStatusLoadPayPending } =
        useRefreshStatusLoadPayMutation();

    useEffect(() => {
        const shouldRefreshLoadPayStatus =
            applicantLoadPayStatus &&
            (applicantLoadPayStatus === DecisionDetailStatuses.UNDERREVIEW_MANUAL ||
                applicantLoadPayStatus === DecisionDetailStatuses.UNDERREVIEW_AUTO);

        if (!applicantId || !shouldRefreshLoadPayStatus || isRefreshStatusLoadPayPending) return;

        if (!hasLoadPayRefreshed) {
            (async () => {
                try {
                    await refreshStatusLoadPay({
                        applicantId: applicantId,
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ['applicant', applicantId],
                    });
                } finally {
                    setHasLoadPayRefreshed(true);
                }
            })();
        }
    }, [applicantId, applicantLoadPayStatus, isRefreshStatusLoadPayPending]);

    // Poll activity every 30s (pauses when tab is unfocused).
    // This is the source of truth for "something happened" — status changes always
    // produce an activity entry, so a change here means we should also re-fetch applicant.
    // TanStack Query deduplicates: ApplicantActivity tab uses the same cache key and
    // receives the updated data automatically — no extra API call.
    const { data: polledActivity } = useApplicantActivityQuery({
        applicantId: applicantId ?? '',
        refetchInterval: 30_000,
    });

    useEffect(
        () =>
            focusManager.subscribe((isFocused) => {
                if (!isFocused || !applicantId) return;
                absorbFocusRefetch.current =
                    queryClient
                        .getQueryCache()
                        .find({ queryKey: ['applicant-activity', applicantId] })
                        ?.isStaleByTime(FOCUS_STALE_TIME_MS) ?? false;
            }),
        [applicantId],
    );

    useEffect(() => {
        if (!polledActivity) return;

        if (previousActivitySnapshot.current === null) {
            // Baseline on initial load — no banner
            previousActivitySnapshot.current = polledActivity;
            return;
        }

        // Always update snapshot so we track the latest state
        const prev = previousActivitySnapshot.current;
        previousActivitySnapshot.current = polledActivity;

        if (Date.now() < suppressBannerUntil.current) return;

        if (absorbFocusRefetch.current) {
            absorbFocusRefetch.current = false;
            return;
        }

        // Compare external activity counts — ignore entries authored by the current
        // reviewer so their own notes/decisions don't trigger the banner for themselves.
        const prevCount = getExternalActivityCount(prev, currentUserEmail);
        const currCount = getExternalActivityCount(polledActivity, currentUserEmail);

        if (currCount <= prevCount) return;

        // External activity grew — something happened that this reviewer didn't do.
        // Re-fetch the main applicant data and show the banner.
        queryClient.invalidateQueries({ queryKey: ['applicant', applicantId] });

        console.log('[ORP] Out of sync banner triggered (external activity changed).', {
            applicantId,
            previousExternalCount: prevCount,
            currentExternalCount: currCount,
            previousActivity: prev,
            currentActivity: polledActivity,
        });
        setShowOutOfSyncBanner(true);
    }, [polledActivity]);

    const handleBannerRefresh = async () => {
        suppressBannerUntil.current = Date.now() + 10_000;
        setShowOutOfSyncBanner(false);
        await queryClient.invalidateQueries();
    };

    // Support browser print shortcut (Cmd+P / Ctrl+P) — mount the aggregate
    // synchronously before the browser captures the DOM for printing.
    useEffect(() => {
        const handleBeforePrint = () => {
            if (isPrintingRef.current) return; // already triggered via button
            isPrintingRef.current = true;
            // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- print must see the mounted aggregate
            flushSync(() => setIsPrinting(true));
        };
        const handleAfterPrint = () => {
            isPrintingRef.current = false;
            setIsPrinting(false);
        };
        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    const {
        data: relatedAccounts,
        isLoading: accountsLoading,
        isLoadingError: accountsLoadingError,
        refetch: refetchRelatedAccounts,
    } = useApplicantRelatedAccountsQuery({
        applicantId: applicantId ?? '',
    });
    const relatedAccountsCount =
        (relatedAccounts?.loadPayAccounts?.length ?? 0) +
        (relatedAccounts?.originationsApplicants?.length ?? 0) +
        (relatedAccounts?.deltaClients?.length ?? 0);
    const isRelatedAccountsCountKnown = !accountsLoading && !accountsLoadingError;
    const isRelatedAccountsCountPartial = (relatedAccounts?.unavailableSources?.length ?? 0) > 0;

    useEffect(() => {
        if (!applicant) return;
        const legalName = applicant.businessInfo?.legalName ?? '';
        const dbaName = applicant.businessInfo?.dbaName ?? '';
        const usDOT = applicant.carrierInfo?.usDOT ?? '';
        const dba = dbaName && dbaName !== legalName ? ` dba ${dbaName}` : '';
        const env = Config.Environment.toLowerCase();
        const envPrefix = env !== 'prod' ? `[${Config.Environment.toUpperCase()}] ` : '';
        document.title = `${envPrefix}${legalName}${dba} USDOT${usDOT} — ORP`;
    }, [applicant]);

    const clearConditionFocus = useCallback(() => {
        if (!searchParams.get('conditionId')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('conditionId');
        next.delete('noteId');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const clearNoteFocus = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('noteId');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const clearNmiFocus = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('nmiId');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const goToDddTab = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', ApplicantTabId.Ddd);
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const tabOptions = useMemo(() => {
        const tabs = [
            {
                id: ApplicantTabId.Application,
                value: <ApplicationTabLabel />,
                content: <ApplicationTab ref={printSectionRef} />,
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.Ddd,
                value: 'DDD',
                content: <DueDiligence ref={printSectionRef} />,
                prefetchQueries: [
                    {
                        queryKey: ['applicant-due-diligence', applicantId],
                        /* c8 ignore next */ queryFn: () =>
                            getApplicantDueDiligence(applicantId ?? ''),
                    },
                ],
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.Activity,
                value: 'Activity',
                content: (
                    <ApplicantActivity
                        isActive={selectedTabId === ApplicantTabId.Activity}
                        focusNoteId={focusActivityNoteId}
                        onNoteFocused={clearNoteFocus}
                        focusNmiId={focusActivityNmiId}
                        onNmiFocused={clearNmiFocus}
                    />
                ),
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.Documents,
                value: 'Documents',
                content: (
                    <ApplicantDocument onDownloadReport={() => handlePrintClickRef.current()} />
                ),
                prefetchQueries: [
                    {
                        queryKey: ['applicant-documents', applicantId],
                        /* c8 ignore next */ queryFn: () =>
                            getApplicantDocuments(applicantId ?? ''),
                    },
                ],
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.Conditions,
                tabTestId: CONDITIONS_TAB_TEST_ID,
                value: (
                    <ApplicantTab>
                        Tasks/Conditions{' '}
                        {isConditionsLoading ? <TabBadgeSpinner strokeWidth="6" /> : null}
                        {isConditionsCountKnown ? (
                            <Tag
                                severity="info"
                                value={openConditionsCount}
                                rounded
                                style={{
                                    fontSize: rem(11),
                                    padding: `${rem(1)} ${rem(6)}`,
                                    ...getTabBadgeStyle(openConditionsCount),
                                }}
                            />
                        ) : null}
                    </ApplicantTab>
                ),
                content: (
                    <ApplicantConditions
                        openConditions={openConditions}
                        closedConditions={closedConditions}
                        deletedConditions={deletedConditions}
                        focusConditionId={focusConditionId}
                        focusNoteId={focusConditionNoteId}
                        onConditionFocused={clearConditionFocus}
                        onNavigateToDdd={goToDddTab}
                        isLoading={isConditionsLoading}
                        isLoadingError={isConditionsLoadingError}
                        onRetry={refetchConditions}
                    />
                ),
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.Tags,
                value: 'Tags/Signals',
                content: <ApplicantTags />,
                prefetchQueries: [
                    {
                        queryKey: ['applicant-tags', applicantId],
                        /* c8 ignore next */ queryFn: () => getApplicantTags(applicantId ?? ''),
                    },
                    {
                        queryKey: ['applicant-reason-codes', applicantId],
                        /* c8 ignore next */ queryFn: () =>
                            getApplicantReasonCodes(applicantId ?? ''),
                    },
                ],
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
            {
                id: ApplicantTabId.RelatedAccounts,
                tabTestId: 'tab-related-accounts',
                value: (
                    <ApplicantTab>
                        Related Entities{' '}
                        {accountsLoading ? <TabBadgeSpinner strokeWidth="6" /> : null}
                        {isRelatedAccountsCountKnown ? (
                            <Tag
                                severity="info"
                                value={relatedAccountsCount}
                                rounded
                                title={
                                    isRelatedAccountsCountPartial
                                        ? RELATED_ACCOUNTS_PARTIAL_COUNT_TITLE
                                        : undefined
                                }
                                style={{
                                    fontSize: rem(11),
                                    padding: `${rem(1)} ${rem(6)}`,
                                    ...(isRelatedAccountsCountPartial
                                        ? TAB_BADGE_PARTIAL_STYLE
                                        : getTabBadgeStyle(relatedAccountsCount)),
                                }}
                            />
                        ) : null}
                    </ApplicantTab>
                ),
                content: (
                    <ApplicantRelatedAccounts
                        loadPayAccounts={relatedAccounts?.loadPayAccounts ?? []}
                        originationsApplicants={relatedAccounts?.originationsApplicants ?? []}
                        deltaClients={relatedAccounts?.deltaClients ?? []}
                        unavailableSources={relatedAccounts?.unavailableSources ?? []}
                        isLoading={accountsLoading}
                        isLoadingError={accountsLoadingError}
                        onRetry={() => refetchRelatedAccounts()}
                    />
                ),
                style: {
                    width: '166px',
                    textAlign: TextAlign.Center,
                },
            },
        ];
        return tabs.map((tab) => ({
            ...tab,
            content: <TabPanel>{tab.content}</TabPanel>,
            defaultSelected: activeTabId === tab.id,
        }));
    }, [
        printSectionRef.current,
        relatedAccounts,
        relatedAccountsCount,
        applicantId,
        openConditions,
        closedConditions,
        deletedConditions,
        accountsLoading,
        accountsLoadingError,
        isRelatedAccountsCountKnown,
        isRelatedAccountsCountPartial,
        refetchRelatedAccounts,
        isConditionsLoading,
        isConditionsLoadingError,
        isConditionsCountKnown,
        refetchConditions,
        activeTabId,
        focusConditionId,
        focusActivityNoteId,
        focusConditionNoteId,
        focusActivityNmiId,
        clearConditionFocus,
        clearNoteFocus,
        clearNmiFocus,
        goToDddTab,
        selectedTabId,
    ]);

    const handlePrintClick = async () => {
        isPrintingRef.current = true;
        setIsPrinting(true);
        try {
            // Prefetch lazy tab data — mirrors the prefetchQueries registry on tabOptions
            await Promise.all([
                /* c8 ignore next */ queryClient.prefetchQuery({
                    queryKey: ['applicant-due-diligence', applicantId],
                    queryFn: () => getApplicantDueDiligence(applicantId ?? ''),
                }),
                /* c8 ignore next */ queryClient.prefetchQuery({
                    queryKey: ['applicant-documents', applicantId],
                    queryFn: () => getApplicantDocuments(applicantId ?? ''),
                }),
                /* c8 ignore next */ queryClient.prefetchQuery({
                    queryKey: ['applicant-tags', applicantId],
                    queryFn: () => getApplicantTags(applicantId ?? ''),
                }),
                /* c8 ignore next */ queryClient.prefetchQuery({
                    queryKey: ['applicant-reason-codes', applicantId],
                    queryFn: () => getApplicantReasonCodes(applicantId ?? ''),
                }),
            ]);
            // Wait for the print aggregate to mount in the DOM before printing
            /* c8 ignore next */ await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
            );
            window.print();
        } finally {
            isPrintingRef.current = false;
            setIsPrinting(false);
        }
    };
    handlePrintClickRef.current = handlePrintClick;

    const handleTabSelected = (tab: { id: string; value: string }) => {
        setSelectedTabId(tab.id);

        if (
            searchParams.get('tab') ||
            searchParams.get('conditionId') ||
            searchParams.get('noteId') ||
            searchParams.get('nmiId') ||
            searchParams.get('submissionId')
        ) {
            const next = new URLSearchParams(searchParams);
            next.delete('tab');
            next.delete('conditionId');
            next.delete('noteId');
            next.delete('nmiId');
            next.delete('submissionId');
            setSearchParams(next, { replace: true });
        }

        // Log tab view
        const userId = authState?.idToken?.claims?.sub;

        const tabEventMap: Record<string, EVENT_LOG_NAMES> = {
            [ApplicantTabId.Application]: EVENT_LOG_NAMES.TabApplicationView,
            [ApplicantTabId.Ddd]: EVENT_LOG_NAMES.TabDDDView,
            [ApplicantTabId.Activity]: EVENT_LOG_NAMES.TabActivityView,
            [ApplicantTabId.Documents]: EVENT_LOG_NAMES.TabDocumentsView,
            [ApplicantTabId.Tags]: EVENT_LOG_NAMES.TabTagsSignalsView,
        };

        const eventName = tabEventMap[tab.id];
        if (eventName) {
            honeybadgerLogEvent(eventName, {
                level: 'INFO',
                customAttributes: {
                    ...getLoggingContext(currentUserEmail, userId),
                    currentPage: window.location.pathname,
                    timestamp: new Date().toISOString(),
                    applicantId,
                    tabName: tab.value,
                },
            });
        }

        if (tab.id === ApplicantTabId.Activity) {
            queryClient.invalidateQueries({
                queryKey: ['applicant-activity', applicantId],
            });
        }
    };

    return (
        <Container>
            <ApplicantHeader
                onPrintClick={handlePrintClick}
                isPrinting={isPrinting}
                onBeforeRefresh={() => {
                    suppressBannerUntil.current = Date.now() + 10_000;
                }}
            />
            <DeepLinkBanner isPageReady={isPageReady} />
            <OutOfSyncBanner show={showOutOfSyncBanner} onRefresh={handleBannerRefresh} />
            <ApplicantStatus />
            <ContentWrapper>
                <Tabs
                    key={tabsRemountKey}
                    onTabSelected={handleTabSelected}
                    // Design System isn't expecting a React element as the Tab Header value, but it works
                    tabs={tabOptions as unknown as React.ComponentProps<typeof Tabs>['tabs']}
                />
            </ContentWrapper>

            {/* Print-only aggregate view — mounted only while printing, revealed by @media print CSS */}
            {isPrinting && (
                <PrintAggregate
                    printDateTime={new Date().toLocaleString()}
                    printUrl={window.location.href}
                    relatedAccounts={relatedAccounts ?? null}
                    openConditions={openConditions}
                    closedConditions={closedConditions}
                    deletedConditions={deletedConditions}
                />
            )}
        </Container>
    );
};
